import type { RowPatch, TaskSummary } from '../../lib/tasks';
import { groupTasks } from '../../lib/taskGroups';
import type { TaskGrouping } from '../../lib/taskGrouping';
import { TASK_STATUS_DOT_CLASSES, TASK_STATUS_LABELS, type TaskStatus } from '../../lib/taskStatus';
import { TASK_GRID } from './taskGrid';
import { TaskComposer } from './TaskComposer';
import { TaskRow } from './TaskRow';

/**
 * Grouped by status, in the explicit TASK_STATUSES order -- never the enum's own
 * ordering, for the reason spelled out in lib/taskStatus.ts. The grouping itself
 * lives in lib/taskGroups.ts, because the page's shift-click needs the same
 * order this renders in.
 *
 * Every group renders, even an empty one, because each carries the inline add
 * row that puts a task straight into that status. The exception is DONE: an
 * empty Done heading is noise, and nobody creates a task that is already done.
 *
 * With `grouping="none"` it is one flat list with no headings, ordered as
 * lib/taskGroups.ts orders it. It keeps a single add row at the foot, which
 * files into TODO -- the same default the New task button and the empty state's
 * composer already use. Dropping the add row along with the headings would have
 * meant that turning grouping off silently took away the only way to type a
 * task straight into the list, which is not what a view switch should do.
 *
 * `onAdd`/`onExpandCompose` are optional, and without them no add row renders
 * at all: a cross-project list has no single project to file a new task into,
 * so it leaves them out and offers a composer that asks for one instead.
 */
export function TaskList({
  tasks,
  activeTaskId,
  selectedIds,
  hrefFor,
  flashId = null,
  grouping = 'status',
  grid = TASK_GRID,
  showProject = false,
  onToggleSelected,
  onPatch,
  onAdd,
  onExpandCompose,
}: {
  tasks: TaskSummary[];
  activeTaskId: string | null;
  selectedIds: ReadonlySet<string>;
  hrefFor: (taskId: string) => string;
  /** The row whose due date was just edited -- see useRowFlash. */
  flashId?: string | null;
  grouping?: TaskGrouping;
  /** The column template every row on this list is drawn on -- see taskGrid.ts.
   *  Must agree with `showProject`, or the header and the rows disagree. */
  grid?: string;
  showProject?: boolean;
  onToggleSelected: (task: TaskSummary, options: { range: boolean }) => void;
  /** One inline field edit. The page owns the request -- see TaskRow. */
  onPatch: (task: TaskSummary, patch: RowPatch) => void;
  /** A title typed into one of the inline rows, with the group it was typed in.
   *  The page posts it; nothing here waits for the answer. Omitted where there
   *  is no single project to add into. */
  onAdd?: (title: string, status: TaskStatus) => void;
  onExpandCompose?: (title: string, status: TaskStatus) => void;
}) {
  const titlesById = new Map(tasks.map((task) => [task.id, task.title]));
  const canAdd = onAdd !== undefined && onExpandCompose !== undefined;

  /* The one header whose track is on the narrow template too, so it cannot be
     `hidden` there -- that would collapse the column the dots below are drawn
     in. `sr-only` alone would do the same (it is `position: absolute`), hence
     the wrapper, the same trick the assignee header uses. */
  const statusHeader = (
    <span>
      <span className="sr-only sm:not-sr-only">Status</span>
    </span>
  );

  return (
    <div>
      {/* The column header. Same template as every row, so the labels sit over
          the columns they name. */}
      <div
        // No bottom border. It used to sit twelve pixels above the first
        // group's own `border-t`, so the top of the list read as two hairlines
        // of different weights for no reason. The group heading between them is
        // already the separation.
        className={`${grid} px-2 pb-1.5 text-micro font-medium uppercase tracking-wide text-stone-500`}
      >
        <span aria-hidden="true" />
        <span>Task</span>
        {/* Status leads the project on the Tasks page and the assignees
            everywhere else, in step with the two templates and with TaskRow --
            see taskGrid.ts. Exactly one of the two branches renders. */}
        {showProject ? statusHeader : null}
        {/* Dropped below `sm:` exactly as the cell is, or the header would
            occupy a track the rows leave empty. */}
        {showProject ? <span className="hidden sm:block">Project</span> : null}
        {/* The label is wrapped rather than being `sr-only` itself: `sr-only`
            is `position: absolute`, which takes the element out of the grid
            flow entirely, so the column it names would collapse and every
            header after it would shift one track left. */}
        <span>
          <span className="sr-only">Assignee</span>
        </span>
        {showProject ? null : statusHeader}
        <span className="hidden sm:block">Due</span>
        <span className="hidden sm:block">Priority</span>
        {/* The row-action track. No label: the buttons under it are hover
            actions that name themselves, and a header word for them would be
            the widest thing in a 2.75rem column. */}
        <span aria-hidden="true" />
      </div>

      {groupTasks(tasks, grouping).map(({ status, tasks: group }) => {
        if (group.length === 0 && status === 'DONE') return null;

        return (
          <section key={status ?? 'all'} className="mt-3">
            {status === null ? null : (
              <h3 className="flex items-center gap-2 px-2 text-meta font-semibold uppercase tracking-wide text-stone-500">
                <span
                  className={`h-1.5 w-1.5 rounded-full ${TASK_STATUS_DOT_CLASSES[status]}`}
                  aria-hidden="true"
                />
                {TASK_STATUS_LABELS[status]}
                <span className="font-normal tabular-nums text-stone-400">{group.length}</span>
              </h3>
            )}

            <ul className="mt-1 divide-y divide-stone-100 border-t border-stone-200">
              {group.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  parentTitle={task.parentId ? (titlesById.get(task.parentId) ?? null) : null}
                  href={hrefFor(task.id)}
                  active={task.id === activeTaskId}
                  selected={selectedIds.has(task.id)}
                  flashing={task.id === flashId}
                  grid={grid}
                  showProject={showProject}
                  onToggleSelected={(options) => onToggleSelected(task, options)}
                  onPatch={(patch) => onPatch(task, patch)}
                />
              ))}

              {/* Nothing under Done -- nobody creates a task that is already
                  finished. Ungrouped there is no status to inherit, so the row
                  files into TODO. */}
              {status === 'DONE' || !canAdd ? null : (
                <TaskComposer
                  variant="row"
                  grid={grid}
                  placeholder="Add task…"
                  onSubmit={(title) => onAdd(title, status ?? 'TODO')}
                  onExpand={(title) => onExpandCompose(title, status ?? 'TODO')}
                />
              )}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
